// ./src/components/features/Events/EventHighlightsField.tsx
//
// Pick the highlights shown on an event — a creatable pill combobox over the
// account's Highlights catalog. Selected highlights render as pills (with their
// icon); options are grouped by category. Typing a name that isn't found offers a
// "Create …" option that opens the full Highlight editor pre-filled (so the
// required group + icon are set), then selects the new highlight. Value is the
// array of selected highlight ids; the event details page syncs the
// event_highlight junction to match on save. Requires a <HighlightsProvider>.
//
// On phones the combobox dropdown is replaced by the full-screen
// OptionPickerModal in multi-select mode (same catalog, grouped, with the
// "create" action pinned on top) — the inline dropdown is too cramped to tap.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Combobox, PillsInput, Pill, Group, Stack, Text, Loader, Box, useCombobox } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { AppIcon, OptionPickerModal } from '@/components/generic';
import { useHighlightsContext, useAppEnvironment } from '@/contexts';
import { HighlightEditDrawer } from '@/components/features/Highlights';
import { Highlight } from '@/utils/api';
import { useCreateInPlace } from '@/hooks/useCreateInPlace';
import { CREATE_IN_OWN_ACCOUNT } from '@/utils/createInPlace';

const CREATE_OPTION = '__create__';

const titleCase = (s: string): string => s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export interface EventHighlightsFieldProps {
  value: string[];
  onChange: (ids: string[]) => void;
}

export const EventHighlightsField: React.FC<EventHighlightsFieldProps> = ({ value, onChange }) => {
  const { t } = useTranslation(['events', 'common']);
  const { highlights, loading } = useHighlightsContext();
  const { isMobile } = useAppEnvironment();

  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
  });

  const byId = useMemo(() => new Map(highlights.map((h) => [h.id, h] as const)), [highlights]);
  const selected = useMemo(() => value.map((id) => byId.get(id)).filter(Boolean) as Highlight[], [value, byId]);

  // Catalog minus the already-selected, filtered by search, grouped by category.
  const grouped = useMemo(() => {
    const chosen = new Set(value);
    const q = search.toLowerCase().trim();
    const groups = new Map<string, Highlight[]>();
    for (const h of highlights) {
      if (!h.id || chosen.has(h.id)) continue;
      if (q && !(h.name ?? '').toLowerCase().includes(q)) continue;
      const g = h.highlightGroup || 'other';
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g)!.push(h);
    }
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [highlights, value, search]);

  const optionCount = grouped.reduce((n, [, items]) => n + items.length, 0);
  const exactExists = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return highlights.some((h) => (h.name ?? '').toLowerCase() === q);
  }, [highlights, search]);
  // #142 — a highlight lands in the caller's own account: never offered to a crew member.
  const canCreate = useCreateInPlace(CREATE_IN_OWN_ACCOUNT);
  const showCreate = canCreate && search.trim().length > 0 && !exactExists;

  const handleSubmit = useCallback(
    (submitted: string) => {
      if (submitted === CREATE_OPTION) {
        setAddName(search.trim());
        setAddOpen(true);
        combobox.closeDropdown();
        setSearch('');
        return;
      }
      onChange([...value, submitted]);
      setSearch('');
      inputRef.current?.focus();
    },
    [search, combobox, onChange, value],
  );

  const handleCreated = useCallback(
    (h: Highlight) => {
      setAddOpen(false);
      if (h?.id) onChange([...value, h.id]);
    },
    [onChange, value],
  );

  const handleRemove = useCallback((id: string) => onChange(value.filter((v) => v !== id)), [onChange, value]);

  const handleToggle = useCallback(
    (id: string) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]),
    [onChange, value],
  );

  // The mobile picker shows the WHOLE catalog (selected rows get a checkmark),
  // grouped by category — unlike the dropdown, which hides what's already picked.
  const pickerData = useMemo(() => {
    const groups = new Map<string, { value: string; label: string }[]>();

    for (const h of highlights) {
      if (!h.id) {
        continue;
      }

      const group = titleCase(h.highlightGroup || 'other');

      if (!groups.has(group)) {
        groups.set(group, []);
      }

      groups.get(group)!.push({ value: h.id, label: h.name ?? '' });
    }

    return [...groups.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([group, items]) => ({ group, items }));
  }, [highlights]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && search === '' && value.length > 0) {
        e.preventDefault();
        onChange(value.slice(0, -1));
      }
    },
    [search, value, onChange],
  );

  return (
    <Stack gap={6}>
      <Text fw={600} size="md" c="gray.8">
        {t('events:highlights.label', { defaultValue: 'Highlights' })}
      </Text>

      {isMobile ? (
        <>
          {/* Tap target styled like an input: the chosen pills (or a placeholder). */}
          <Box
            onClick={() => setPickerOpen(true)}
            px="sm"
            py={8}
            style={{
              border: '1px solid var(--mantine-color-gray-4)',
              borderRadius: 'var(--mantine-radius-sm)',
              cursor: 'pointer',
              minHeight: 42,
            }}
          >
            <Group gap={6} wrap="wrap">
              {selected.length === 0 ? (
                <Text size="md" c="dimmed">
                  {t('events:highlights.placeholder', { defaultValue: 'Search or add highlights…' })}
                </Text>
              ) : (
                selected.map((h) => (
                  <Pill
                    key={h.id}
                    withRemoveButton
                    onRemove={() => handleRemove(h.id as string)}
                    size="md"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Group gap={4} wrap="nowrap">
                      <AppIcon iconName={h.icon || 'Star'} size={13} />
                      <span>{h.name}</span>
                    </Group>
                  </Pill>
                ))
              )}
              {loading && <Loader size={14} />}
            </Group>
          </Box>

          <OptionPickerModal
            opened={pickerOpen}
            onClose={() => setPickerOpen(false)}
            data={pickerData}
            multiple
            values={value}
            onToggle={handleToggle}
            title={t('events:highlights.label', { defaultValue: 'Highlights' })}
            searchPlaceholder={t('events:highlights.search', { defaultValue: 'Search highlights…' })}
            createLabel={canCreate ? t('events:highlights.createNew', { defaultValue: 'Create a highlight' }) : undefined}
            onCreate={
              canCreate
                ? (text) => {
                    setAddName(text);
                    setPickerOpen(false);
                    setAddOpen(true);
                  }
                : undefined
            }
            nothingFoundMessage={t('events:highlights.noMatch', { defaultValue: 'No highlights found' })}
            doneLabel={t('common:buttons.done', { defaultValue: 'Done' })}
          />
        </>
      ) : (
        <Combobox store={combobox} onOptionSubmit={handleSubmit} withinPortal>
          <Combobox.DropdownTarget>
            <PillsInput onClick={() => combobox.openDropdown()} rightSection={loading ? <Loader size={14} /> : undefined}>
              <Pill.Group>
                {selected.map((h) => (
                  <Pill key={h.id} withRemoveButton onRemove={() => handleRemove(h.id as string)} size="md">
                    <Group gap={4} wrap="nowrap">
                      <AppIcon iconName={h.icon || 'Star'} size={13} />
                      <span>{h.name}</span>
                    </Group>
                  </Pill>
                ))}

                <Combobox.EventsTarget>
                  <PillsInput.Field
                    ref={inputRef}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.currentTarget.value);
                      combobox.openDropdown();
                      combobox.updateSelectedOptionIndex();
                    }}
                    onFocus={() => combobox.openDropdown()}
                    onBlur={() => combobox.closeDropdown()}
                    onKeyDown={handleKeyDown}
                    placeholder={value.length === 0 ? t('events:highlights.placeholder', { defaultValue: 'Search or add highlights…' }) : undefined}
                  />
                </Combobox.EventsTarget>
              </Pill.Group>
            </PillsInput>
          </Combobox.DropdownTarget>

          <Combobox.Dropdown>
            <Combobox.Options mah={280} style={{ overflowY: 'auto' }}>
              {grouped.map(([group, items]) => (
                <Combobox.Group key={group} label={titleCase(group)}>
                  {items.map((h) => (
                    <Combobox.Option key={h.id} value={h.id as string}>
                      <Group gap="sm" wrap="nowrap">
                        <AppIcon iconName={h.icon || 'Star'} size={16} />
                        <Text size="md" lineClamp={1}>
                          {h.name}
                        </Text>
                      </Group>
                    </Combobox.Option>
                  ))}
                </Combobox.Group>
              ))}

              {showCreate && (
                <Combobox.Option value={CREATE_OPTION}>
                  <Group gap="sm" wrap="nowrap">
                    <AppIcon iconName="Plus" size={16} />
                    <Text size="md">
                      {t('events:highlights.createOption', { defaultValue: 'Create “{{name}}”…', name: search.trim() })}
                    </Text>
                  </Group>
                </Combobox.Option>
              )}

              {!showCreate && optionCount === 0 && (
                <Text size="md" c="dimmed" ta="center" p="sm">
                  {loading
                    ? t('common:loading', { defaultValue: 'Loading…' })
                    : t('events:highlights.noMatch', { defaultValue: 'No highlights found' })}
                </Text>
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
      )}

      <Text size="md" c="dimmed">
        {t('events:highlights.hint', { defaultValue: 'Badges shown on your event page — pick a few or type to add your own.' })}
      </Text>

      <HighlightEditDrawer opened={addOpen} onClose={() => setAddOpen(false)} initialName={addName} onCreated={handleCreated} />
    </Stack>
  );
};
